import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import css from './Internal.css';
import Icon from 'SF/MaterialIcon';
import StyledButton from 'common/StyledButton';
import Select from 'common/StyledSelect';
import useStarfishAPI from 'hooks/useStarfishAPI';
import { urlParams } from 'utils/request';
import { isEmpty, sortBy } from 'lodash';

const NotAllowed = () => (
  <div className={css['container']}>
    Internal links are not allowed here.
  </div>
);

const AdditionalDropdown = ({label, isLoading, dataOptions, selectedData, setSelectedData}) => {
 return  <div className={`${css['secondary-action-container']} ${css['action-container']}`}>
    <label htmlFor="internal-link-data-select">{label}</label>
    <Select
        id="internal-link-data-select"
        value={selectedData}
        onChange={option => setSelectedData(option)}
        isLoading={isLoading}
        options={dataOptions}
    />
  </div>
}

AdditionalDropdown.propTypes = {
  label: PropTypes.string,
  selectedData: PropTypes.object,
  setSelectedData: PropTypes.func,
  isLoading: PropTypes.bool,
  dataOptions: PropTypes.array
}

const Internal = props => {

  const notAllowed = (
    props.externalLinksOnly // Do not show internal tab if external links only
    || !props.hasSSOMethod // Or if user hasn't set a SSO method
  );

  if ( notAllowed ) return <NotAllowed />;

  const [selectedAction, setSelectedAction] = useState(null);
  const [actionResponse, callActionAPI] = useStarfishAPI({isNewAPI: true});
  const [actionsOptions, setActionsOptions] = useState([]);
  const selectRef = useRef(null);

  const [selectedData, setSelectedData] = useState(null);
  const [dataResponse, callDataAPI] = useStarfishAPI({isNewAPI: true});
  const [dataOptions, setDataOptions] = useState([]);

  useEffect(() => {
    const params = urlParams({messageTemplateType: props.messageTemplateType}, false);
    // Replace URL with actual endpoint
    const URL = `../api/message-template-actions?${params}`;
    callActionAPI(URL);
  }, []);

  useEffect(() => {
    // If no option selected, we clear the internal URL
    if ( !selectedAction ) {
      props.setInternalLink({});
      return;
    }

    // If that's the case, we don't need a second dropdown
    if ( !selectedAction.actionDataEndpoint ) {
      props.setInternalLink({
        url: selectedAction.endUserUrl,
        action: selectedAction.label
      });
    } else {
      props.setInternalLink({});
      setSelectedData(null);
      callDataAPI(selectedAction.actionDataEndpoint);
    }
  }, [selectedAction]);

  useEffect(() => {
    if ( !selectedData ) return;

    const id = selectedData.value;
    const URL = selectedAction.endUserUrl.replace("{id}", encodeURIComponent(id));
    props.setInternalLink({
      url: URL,
      action: selectedAction.label
    });
  }, [selectedData]);

  useEffect(() => {
    if ( isEmpty(actionResponse.data) ) return;
    
    const options = actionResponse.data.map(action => ({
      value: action.id,
      label: action.action,
      endUserUrl: action.endUserUrl,
      actionDataEndpoint: action.actionDataEndpoint
    }));
    setActionsOptions(options);
  }, [actionResponse]);

  useEffect(() => {
    if ( isEmpty(dataResponse.data) ) return;
    // in case of error, clear up previous dropdown options
    if (!dataResponse.pending && !dataResponse.success) {
      setDataOptions([]);
      return;
    }
    
    const options = dataResponse.data
      .map(({id, name}) => ({
        label: name,
        value: id
      }))
      .sort((a, b) => a.label < b.label ? -1 : 1);
    setDataOptions(options);
  }, [dataResponse]);

  const showAdditionalDropdown = selectedAction && selectedAction.actionDataEndpoint;
  // Currently this error message wording is specific to the notifications widget. If we add other widgets as options,
  // we should make this warning more generic and use a regex here such as:
  // selectedAction && /student dashboard/.test(selectedAction.label)
  const showDashboardWarning = selectedAction && selectedAction.label === 'View student Dashboard notifications';

  return (
    <div className={css['internal-page-container']}>
      <div className={css['dropdown-container']}>
        <div className={css['action-container']}>
          <label htmlFor="internal-link-action-select">Choose an action:</label>
          <Select
            id="internal-link-action-select"
            ref={selectRef}
            value={selectedAction} 
            onChange={option => setSelectedAction(option)}
            isLoading={actionResponse.pending}
            options={sortBy(actionsOptions, [action => action.label.toLowerCase()])}
          />
        </div>
        {showAdditionalDropdown &&
          <AdditionalDropdown label={selectedAction.label === 'Check in to a Kiosk' ? 'Choose a kiosk:' : 'Choose a service:'}
                            isLoading={dataResponse.isLoading}
                            dataOptions={dataOptions}
                            selectedData={selectedData}
                            setSelectedData={setSelectedData}
          />}
        {showDashboardWarning &&
        <div className={css['dashboard-link-info-container']}>
          <Icon aria-hidden="true" icon="info" className={css['info-icon']} />
          <span data-testid="dashboard-link-info-message">The Dashboard Notifications widget should be enabled;
            otherwise, this link will go to the Dashboard. If the Dashboard is disabled, this link will go to the
            My Success Network page.</span>
        </div>}
      </div>
      <div className={css['clear-button-container']}>
        {selectedAction && <StyledButton
          asLink
          onClick={() => {
            setSelectedAction(null);
            setSelectedData(null);
            selectRef.current.focus();
          }}>
          Clear
        </StyledButton>}
      </div>
    </div>
  );
}

Internal.propTypes = {
  externalLinksOnly: PropTypes.bool.isRequired,
  setInternalLink: PropTypes.func.isRequired,
  messageTemplateType: PropTypes.string,
  hasSSOMethod: PropTypes.bool.isRequired,
};

export default Internal;
